import { useCallback, useEffect, useMemo, useState } from "react";

import { Ratio } from "@simplyk/common";

import { getSizeInBite } from "../utils";

export interface ImageMetaData {
  width: number;
  height: number;
  size: number;
  extension: string;
}

interface UseImageMetadataParams {
  fileUrl: string | null | undefined;
  ratio: Ratio;
  displayAspectRatio: number | undefined;
}

export const useImageMetadata = ({ fileUrl, ratio, displayAspectRatio }: UseImageMetadataParams) => {
  const [actualImageMetaData, setActualImageMetaData] = useState<ImageMetaData | undefined>(undefined);

  const fillImageMetadata = useCallback((url: string) => {
    const isBlob = url.startsWith("data:");
    const extension = (isBlob ? url.split(";")[0].split("image/").pop() : url.split(".").pop()) ?? "none";
    const img = new Image();
    img.src = url;
    img.onload = function () {
      setActualImageMetaData({
        width: img.naturalWidth,
        height: img.naturalHeight,
        extension,
        size: getSizeInBite(url),
      });
    };
  }, []);

  useEffect(() => {
    if (fileUrl && !actualImageMetaData) {
      fillImageMetadata(fileUrl);
    }
  }, [fileUrl, actualImageMetaData, fillImageMetadata]);

  const ratioValue = useMemo((): number | undefined => {
    if (ratio === Ratio.Free) {
      return undefined;
    }
    if (ratio === Ratio.Square) {
      return 1;
    }
    if (ratio === Ratio.Auto) {
      if (actualImageMetaData && actualImageMetaData.width && actualImageMetaData.height) {
        return actualImageMetaData.width / actualImageMetaData.height;
      }
      return 16 / 9;
    }
    return 16 / 9;
  }, [ratio, actualImageMetaData]);

  const displayRatioValue = displayAspectRatio ?? ratioValue;

  return { actualImageMetaData, setActualImageMetaData, fillImageMetadata, ratioValue, displayRatioValue };
};
